import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  MenuItem,
  Select,
  TextField,
  Typography,
  makeStyles,
} from "@material-ui/core";
import React, { useEffect, useState } from "react";
import { TEXT } from "Clients/ACCB/ACCBTheme";
import { format } from "date-fns";
import DatePickerACCB from "../DatePickerACCB";
import FormError from "../FormError";
import { getAwardParties, updateFeeCalculation } from "../../service";
import { isAllowedAmountInput, parseDateString } from "../../utils";

const useStyles = makeStyles((theme) => ({
  label: {
    fontWeight: 500,
    fontSize: "0.875rem",
    marginBottom: theme.spacing(1),
    color: TEXT.secondary,
    display: "block",
  },
  caseCard: {
    padding: "0.75rem 1rem",
    backgroundColor: "#f5f5f5",
    borderRadius: 6,
    border: "1px solid #e0e0e0",
    marginBottom: "1.25rem",
  },
  readOnlyValue: {
    fontSize: "0.875rem",
    fontWeight: 600,
    color: TEXT.strong,
  },
}));

export default function EditFeeCalculationDialog({ open, entry, onClose, onSaved }) {
  const classes = useStyles();

  const [claimAmount, setClaimAmount] = useState("");
  const [totalAmount, setTotalAmount] = useState("");
  const [amountPaid, setAmountPaid] = useState("");
  const [receivedBy, setReceivedBy] = useState("");
  const [receiptDate, setReceiptDate] = useState(null);
  const [challanNumber, setChallanNumber] = useState("");

  const [parties, setParties] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Re-seed every time a different row is opened.
  useEffect(() => {
    if (!entry) return;
    setClaimAmount(entry.claim_amount != null ? String(entry.claim_amount) : "");
    setTotalAmount(
      entry.total_fee != null
        ? String(entry.total_fee)
        : entry.total_amount != null
          ? String(entry.total_amount)
          : ""
    );
    setAmountPaid(entry.amount_paid != null ? String(entry.amount_paid) : "");
    setReceivedBy(entry.received_by?.id || "");
    setReceiptDate(entry.receipt_date ? parseDateString(entry.receipt_date) : null);
    setChallanNumber(entry.challan_number || "");
    setError("");
  }, [entry]);

  // "Received By" is picked from the parties on the case, same as the manual
  // financial entry form.
  useEffect(() => {
    if (!entry?.dispute_id) {
      setParties([]);
      return;
    }
    getAwardParties(entry.dispute_id)
      .then((res) => setParties(res.data?.parties || []))
      .catch(() => setParties([]));
  }, [entry]);

  // Keep the currently-saved receiver selectable even when they are not in the
  // parties list the API returns.
  const receiverOptions = [...parties];
  if (receivedBy && !receiverOptions.some((o) => o.profile_id === receivedBy)) {
    receiverOptions.push({
      profile_id: receivedBy,
      label: entry?.received_by?.name || "Selected Profile",
    });
  }

  const handleSave = () => {
    setError("");

    const claim = parseFloat(claimAmount);
    if (isNaN(claim) || claim <= 0) {
      setError("Please enter a valid sum in dispute.");
      return;
    }
    const total = parseFloat(totalAmount);
    if (isNaN(total) || total <= 0) {
      setError("Please enter a valid total fee.");
      return;
    }
    if (amountPaid !== "" && isNaN(parseFloat(amountPaid))) {
      setError("Please enter a valid amount paid.");
      return;
    }

    setSaving(true);
    updateFeeCalculation({
      id: entry.id,
      claim_amount: claim,
      total_amount: total,
      amount_paid: amountPaid === "" ? null : parseFloat(amountPaid),
      received_by: receivedBy || null,
      receipt_date: receiptDate ? format(receiptDate, "yyyy-MM-dd") : null,
      challan_number: challanNumber || null,
    })
      .then((res) => {
        setSaving(false);
        if (onSaved) onSaved(res.data);
      })
      .catch((err) => {
        console.error("Fee calculation update failed:", err);
        const data = err?.response?.data;
        setError(
          data?.message ||
            data?.error ||
            data?.detail ||
            "Failed to update the fee calculation. Please try again."
        );
        setSaving(false);
      });
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Fee Calculation</DialogTitle>
      <DialogContent dividers>
        {entry && (
          <Box className={classes.caseCard}>
            <Typography variant="caption" color="textSecondary">
              Case
            </Typography>
            <Typography className={classes.readOnlyValue}>
              {entry.case_number || entry.dispute_id || "—"}
              {entry.case_name ? ` — ${entry.case_name}` : ""}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              No. of Prayers: {entry.number_of_prayers ?? (entry.prayers || []).length}
            </Typography>
          </Box>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography className={classes.label}>Sum in Dispute (INR)</Typography>
            <TextField
              variant="outlined"
              size="small"
              fullWidth
              type="number"
              value={claimAmount}
              onChange={(e) => {
                if (isAllowedAmountInput(e.target.value)) setClaimAmount(e.target.value);
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography className={classes.label}>Total Fee (INR)</Typography>
            <TextField
              variant="outlined"
              size="small"
              fullWidth
              type="number"
              value={totalAmount}
              onChange={(e) => {
                if (isAllowedAmountInput(e.target.value)) setTotalAmount(e.target.value);
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography className={classes.label}>Amount Paid (INR)</Typography>
            <TextField
              variant="outlined"
              size="small"
              fullWidth
              type="number"
              value={amountPaid}
              onChange={(e) => {
                if (isAllowedAmountInput(e.target.value)) setAmountPaid(e.target.value);
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography className={classes.label}>Received By</Typography>
            <FormControl variant="outlined" size="small" fullWidth>
              <Select
                value={receivedBy}
                onChange={(e) => setReceivedBy(e.target.value)}
                displayEmpty
              >
                <MenuItem value="">
                  <em>Select...</em>
                </MenuItem>
                {receiverOptions.map((opt) => (
                  <MenuItem key={opt.profile_id} value={opt.profile_id}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography className={classes.label}>Date of Receipt</Typography>
            <DatePickerACCB
              label=""
              value={receiptDate}
              onChange={setReceiptDate}
              maxDate={new Date()}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography className={classes.label}>Challan Number</Typography>
            <TextField
              variant="outlined"
              size="small"
              fullWidth
              value={challanNumber}
              onChange={(e) => setChallanNumber(e.target.value)}
            />
          </Grid>
        </Grid>

        <FormError message={error} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving} size="small">
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={saving}
          size="small"
        >
          {saving ? <CircularProgress size={20} color="inherit" /> : "Save Changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
